// frontend/src/pages/CalendarioPacientePage.jsx

import React, { useState, useEffect, useContext } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './CalendarStyles.css';
import api from '../services/api';
import toast from 'react-hot-toast';
import { isSameDay, formatDate } from '../utils/dateUtils';
import AuthContext from '../context/AuthContext';
import { Clock, CheckCircle2, Circle } from 'lucide-react';

export default function CalendarioPacientePage() {
  const { user } = useContext(AuthContext);
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    buscarMinhasSessoes();
    const interval = setInterval(buscarMinhasSessoes, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const buscarMinhasSessoes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/meu-dashboard');
      setSessoes(response.data.data.sessions || []);
    } catch (error) {
      toast.error('Erro ao carregar sessões');
    } finally {
      setLoading(false);
    }
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const sessoesDoDia = sessoes.filter(s => s.sessionDate && isSameDay(s.sessionDate, date));
      if (sessoesDoDia.length === 0) return null;
      const temAvaliada = sessoesDoDia.some(s => s.evolution);
      const temPendente = sessoesDoDia.some(s => !s.evolution);
      return (
        <div className="flex justify-center items-center gap-0.5 mt-1">
          {temAvaliada && <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>}
          {temPendente && <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>}
        </div>
      );
    }
    return null;
  };

  const sessoesDoDiaSelecionado = sessoes.filter(s => s.sessionDate && isSameDay(s.sessionDate, dataSelecionada));
  const totalAgendadas = sessoes.filter(s => s.sessionDate).length;
  const totalAvaliadas = sessoes.filter(s => s.evolution).length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Meu Calendário</h1>
        <p className="text-gray-500 mt-1">Acompanhe suas sessões agendadas</p>
      </div>

      {loading && (
        <div className="flex justify-center mb-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

        {/* Calendário */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col" style={{ minHeight: '520px' }}>
          <Calendar
            onChange={setDataSelecionada}
            value={dataSelecionada}
            tileContent={tileContent}
            className="w-full border-0 flex-1"
          />
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span>
              <span>{totalAgendadas} sessão{totalAgendadas !== 1 ? 'ões' : ''} agendada{totalAgendadas !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block"></span>
              <span>{totalAvaliadas} avaliada{totalAvaliadas !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* Painel lateral */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" style={{ minHeight: '520px' }}>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {dataSelecionada.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </h2>
            {sessoesDoDiaSelecionado.length > 0 && (
              <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                {sessoesDoDiaSelecionado.length} sessão{sessoesDoDiaSelecionado.length !== 1 ? 'ões' : ''}
              </span>
            )}
          </div>

          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
            {sessoesDoDiaSelecionado.length > 0 ? (
              sessoesDoDiaSelecionado.map(sessao => (
                <div
                  key={sessao._id}
                  className={`p-4 rounded-xl border transition-all
                    ${sessao.evolution
                      ? 'border-green-200 bg-green-50'
                      : 'border-gray-100 bg-white'
                    }`}
                >
                  <div className="flex items-start gap-3">
                    {sessao.sessionTime && (
                      <p className={`font-bold text-base leading-tight flex-shrink-0 ${sessao.evolution ? 'text-green-600' : 'text-blue-600'}`}>
                        {sessao.sessionTime}
                      </p>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-gray-900 text-sm">
                          Sessão #{sessao.sessionNumber}
                        </p>
                        {sessao.evolution
                          ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          : <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        }
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDate(sessao.sessionDate)}
                      </p>
                      <p className={`text-xs mt-1 font-medium ${sessao.evolution ? 'text-green-600' : 'text-gray-400'}`}>
                        {sessao.evolution ? 'Evolução registrada' : 'Aguardando evolução'}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Clock className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">Nenhuma sessão neste dia</p>
                <p className="text-xs text-gray-400 mt-1">Selecione um dia marcado no calendário</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}